
import CommonMixin from '@/core/pages/CommonMixin';
import * as TelliqService from '@/services/telliq.service';
import { CREATE_BOOKING_DATE_FORMAT, CREATE_BOOKING_TIME_FORMAT, emitter } from '@/utils/constants';
import moment from 'moment';
import MainButton from '@/core/components/MainButton.vue';
import Component from 'vue-class-component';
import { Booking, CreateBooking, Machine } from '@/models/bookings';
import { MUTATIONS } from '@/store/mutations';
import { Driver } from '@/models/users';
import { GETTERS } from '@/store/getters';
import { User } from '@/models/auth';

@Component({
  components: {
    MainButton,
  },
})
export default class InstantBooking extends CommonMixin {
  last_booking?: Booking;
  driver?: Driver;

  async created(): Promise<void> {
    try {
      this.loading = true;

      const machine_id = +this.$route.params.id;

      const start_date = moment();
      const [machines, bookings] = await Promise.all([
        TelliqService.getMachines(),
        TelliqService.getBookings(
          start_date.clone().subtract(15, 'days').toISOString(),
          start_date.clone().add(15, 'days').toISOString(),
          [machine_id],
        ),
      ]);

      const machine = await this.findMachineOrBackHome(machines, machine_id);
      if (!machine) {
        return;
      }

      const create_booking: CreateBooking = {
        startDate: start_date.format(CREATE_BOOKING_DATE_FORMAT),
        startTime: start_date.format(CREATE_BOOKING_TIME_FORMAT),
        machine,
      };
      this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, create_booking);
      // This is a workaround to enable date and time picker colours.
      // this.$store.commit(MUTATIONS.SET_EDIT_BOOKING, '');

      this.$store.commit(MUTATIONS.SET_HEADER, {
        title: this.$t('bookings.labels.car_name'),
        subtitle: machine.name,
      });

      const overlapping_booking = this.findOverlappingBooking(bookings, start_date.toISOString());
      if (overlapping_booking) {
        this.last_booking = overlapping_booking;
        const drivers = await TelliqService.getDrivers();
        this.driver = drivers.find((driver) => driver.driver.id === overlapping_booking.driverId);
      } else {
        create_booking.canBePrivate = machine.isPrivateBookingEnabled;
        this.$store.commit(MUTATIONS.SET_INSTANT_BOOKING, true);
        this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, create_booking);
        this.$router.push('/booking/end-date');
      }
    } catch {
      emitter.emit('notify', {
        text: this.$t('core.errors.something_went_wrong'),
        color: 'error',
      });
    } finally {
      this.loading = false;
    }
  }

  findOverlappingBooking(bookings: Booking[], time: string): Booking | undefined {
    return bookings.find((booking) => moment(time).isBetween(booking.startDate, booking.endDate));
  }

  async findMachineOrBackHome(machines: Machine[], machine_id: number): Promise<Machine | undefined> {
    const machine = machines.find((machine) => machine.id === machine_id);

    if (!machine) {
      emitter.emit('notify', {
        text: this.$t('bookings.snackbars.vehicle_not_found'),
        color: 'error',
      });
      await this.$router.push('/');
      return;
    }

    // Machine is check-in only.
    if (machine.isEnabledCheckingFunctionality === true) {
      emitter.emit('notify', {
        text: this.$t('bookings.snackbars.vehicle_check_in_booking_only'),
        color: 'warning',
      });
      await this.$router.push('/');
      return;
    }

    return machine;
  }

  get lastBookingTime(): string {
    return this.last_booking ? moment(this.last_booking.endDate).format('DD.MM.YYYY HH:mm') : '';
  }

  get lastBookingDriverName(): string {
    if (this.driver) {
      return this.driver.driver.name;
    }
    const user: User = this.$store.getters[GETTERS.GET_USER];
    if (user) {
      return user.firstName + ' ' + user.lastName;
    }
    return '';
  }

  // searchOtherCars(): void {
  //   this.$router.push('/booking/car-selection');
  // }

  findNextAvailableTime(): void {
    this.$router.push('/booking/start-date');
  }
}
